import {
  isPublicGameWithTeams,
  type PublicGame,
} from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import { getPlayerTeamFirstMatchup } from '~/domains/contest/domains/services/utils/playerTeamMatchup';

import type { PublicMarketPlayerProp } from '../services/interfaces/market.types';

const NAME_SEPARATOR = '·';
const DETAIL_SEPARATOR = '•';

type PlayerPropTitleMarket = Pick<
  PublicMarketPlayerProp,
  'line' | 'yes_line_display' | 'prop_type_display' | 'team_id' | 'sub_category'
>;

export type PlayerPropTitleParts = {
  title: string;
  subtitle: string;
};

function buildMatchup(
  market: PlayerPropTitleMarket,
  event: PublicGame | undefined,
  homeTeam: PublicTeam | undefined,
  awayTeam: PublicTeam | undefined
): string | undefined {
  if (!event || !isPublicGameWithTeams(event)) return undefined;

  const matchup = getPlayerTeamFirstMatchup({
    awayTeam: { alias: awayTeam?.alias ?? event.away.alias, id: event.away.id },
    homeTeam: { alias: homeTeam?.alias ?? event.home.alias, id: event.home.id },
    playerTeamId: market.team_id,
    league: market.sub_category,
  });
  if (!matchup) return undefined;

  return `${matchup.leftAlias} ${matchup.separator} ${matchup.rightAlias}`;
}

export function buildPlayerPropTitle(params: {
  market: PlayerPropTitleMarket;
  sideLabel: string;
  playerName: string | undefined;
  event: PublicGame | undefined;
  homeTeam: PublicTeam | undefined;
  awayTeam: PublicTeam | undefined;
}): PlayerPropTitleParts {
  const { market, sideLabel, playerName, event, homeTeam, awayTeam } = params;

  const name = playerName?.trim();
  const title = name ? `${name} ${NAME_SEPARATOR} ${sideLabel}` : sideLabel;

  const lineDisplay = market.line != null ? market.yes_line_display : undefined;
  const stat = [lineDisplay, market.prop_type_display]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' ');

  const matchup = buildMatchup(market, event, homeTeam, awayTeam);

  const subtitle = [stat, matchup]
    .filter((part): part is string => Boolean(part))
    .join(` ${DETAIL_SEPARATOR} `);

  return { title, subtitle };
}
